import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Info, ChevronDown } from 'lucide-react';
export function BusinessModel() {
  const [showAssumptions, setShowAssumptions] = useState(false);
  return <section id="pricing" className="py-24 border-b border-stone-300" data-id="element-1214">
      <div className="max-w-6xl mx-auto px-6" data-id="element-1215">
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1216">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16" data-id="element-1217">
            {/* Left: Header */}
            <div className="lg:col-span-5" data-id="element-1218">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-1219">
                Business Model
              </p>

              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.1] text-stone-900 mb-6" data-id="element-1220">
                We win when you win
              </h2>

              <p className="text-2xl text-red-600 font-mono mb-6" data-id="element-1221">
                5% of verified net savings
              </p>

              <p className="text-lg text-stone-600 leading-relaxed mb-8" data-id="element-1222">
                We deploy the platform free or near-free. We establish a
                baseline from your trailing 12 months. We measure the wholesale
                leakage eliminated. We take 5% of the verified net savings.
                That's it.
              </p>

              <div className="space-y-3 text-sm text-stone-500" data-id="element-1223">
                <div className="flex items-start gap-2" data-id="element-1224">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-stone-400" data-id="element-1225" />
                  <span data-id="element-1226">
                    <strong className="text-stone-700" data-id="element-1227">Savings</strong> = Group
                    Room Revenue × Wholesale Leakage % × Adoption %
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-1228">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-stone-400" data-id="element-1229" />
                  <span data-id="element-1230">
                    <strong className="text-stone-700" data-id="element-1231">BQ Share</strong> =
                    Savings × 5%
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Financial breakdown */}
            <div className="lg:col-span-7" data-id="element-1232">
              <div className="border border-stone-300" data-id="element-1233">
                <div className="p-6 border-b border-stone-300 bg-stone-100" data-id="element-1234">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-1235">
                    Financial Example — Base Case
                  </p>
                </div>

                {/* Inputs */}
                <div className="p-6 border-b border-stone-300" data-id="element-1236">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-1237">
                    Inputs
                  </p>
                  <div className="grid grid-cols-3 gap-4 font-mono text-sm" data-id="element-1238">
                    <div data-id="element-1239">
                      <p className="text-stone-500 mb-1" data-id="element-1240">Group Room Revenue</p>
                      <p className="text-stone-900 text-lg" data-id="element-1241">$50M</p>
                      <p className="text-xs text-stone-400 mt-1" data-id="element-1242">
                        Annual group bookings
                      </p>
                    </div>
                    <div data-id="element-1243">
                      <p className="text-stone-500 mb-1" data-id="element-1244">Wholesale Leakage</p>
                      <p className="text-stone-900 text-lg" data-id="element-1245">20%</p>
                      <p className="text-xs text-stone-400 mt-1" data-id="element-1246">
                        Revenue lost to wholesalers
                      </p>
                    </div>
                    <div data-id="element-1247">
                      <p className="text-stone-500 mb-1" data-id="element-1248">Adoption</p>
                      <p className="text-stone-900 text-lg" data-id="element-1249">60%</p>
                      <p className="text-xs text-stone-400 mt-1" data-id="element-1250">
                        Volume routed via BlockQuote
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-stone-200" data-id="element-1251">
                    <p className="font-mono text-xs text-stone-400 mb-2" data-id="element-1252">
                      $50M × 20% × 60% =
                    </p>
                    <p className="font-mono text-2xl text-stone-900" data-id="element-1253">
                      $6.0M{' '}
                      <span className="text-sm text-stone-500" data-id="element-1254">
                        estimated annual savings
                      </span>
                    </p>
                  </div>
                </div>

                {/* With BlockQuote */}
                <div className="p-6 border-b border-stone-300" data-id="element-1255">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-1256">
                    With BlockQuote
                  </p>
                  <div className="grid grid-cols-2 gap-8" data-id="element-1257">
                    <div data-id="element-1258">
                      <p className="font-mono text-xs text-stone-500 mb-2" data-id="element-1259">
                        Resort Keeps (95%)
                      </p>
                      <p className="font-mono text-3xl text-stone-900" data-id="element-1260">$5.7M</p>
                      <p className="text-sm text-stone-500 mt-1" data-id="element-1261">
                        Pure profit to bottom line
                      </p>
                    </div>
                    <div data-id="element-1262">
                      <p className="font-mono text-xs text-stone-500 mb-2" data-id="element-1263">
                        BlockQuote Fee (5%)
                      </p>
                      <p className="font-mono text-3xl text-red-600" data-id="element-1264">$300K</p>
                      <p className="text-sm text-stone-500 mt-1" data-id="element-1265">
                        Only on verified savings
                      </p>
                    </div>
                  </div>
                </div>

                {/* Note */}
                <div className="p-4 bg-stone-50" data-id="element-1266">
                  <p className="font-mono text-xs text-stone-500" data-id="element-1267">
                    * Example per resort group. Scales with portfolio size.
                  </p>
                </div>
              </div>

              {/* Assumptions Toggle */}
              <button onClick={() => setShowAssumptions(!showAssumptions)} className="mt-4 flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700 transition-colors" data-id="element-1268">
                <ChevronDown className={`w-4 h-4 transition-transform ${showAssumptions ? 'rotate-180' : ''}`} data-id="element-1269" />
                Assumptions
              </button>
              {showAssumptions && <div className="mt-3 p-4 bg-stone-50 border border-stone-200 text-sm text-stone-500 space-y-2" data-id="element-1270">
                  <p data-id="element-1271">
                    • Savings are calculated from verified leakage reduction on
                    group room revenue.
                  </p>
                  <p data-id="element-1272">
                    • Adoption % reflects the portion of group volume routed
                    through BlockQuote.ai.
                  </p>
                  <p data-id="element-1273">
                    • Actual results vary by seasonality, segment, and
                    integration depth.
                  </p>
                </div>}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}